import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { apiRequest } from "../components/lib/queryClient";
import { Play, Check, TrendingUp, TrendingDown, BarChart3, Brain, Activity, Sparkles, Target, Loader2, Users, ChevronDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { useToast } from "../hooks/useToast";
import { useAuth } from "../components/features/AuthContext";
import { ApiResponse } from "../components/lib/utils";
import z from "zod";
import { Form, FormControl, FormField, FormItem } from "../components/ui/form";
import { Checkbox } from "../components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ResultGrid } from "../components/ResultGrid";
import SortableGPTable from "../components/sortable_table";


interface Bucket {
    _id: string;
    name: string;
    pairs?: string[];
    stocks?: { underlying_symbol?: string }[];
    category: "stocks" | "crypto";
    userId: string;
    type: "user" | "common";
    updatedAt?: string;
}

export interface FilterResultStockSchema {
    symbol: string,
    filter_name?: string,           // missing on failed stocks
    variables?: Record<string, number>,  // missing on failed stocks
    passed: boolean,
    // stocks-only fields (not present in crypto mode)
    fno_active?: boolean,
    security_id?: number,
    // crypto response fields
    filter_id?: string,
    category?: string,
    timestamp?: string,
}

interface SentimentStockData {
    sentiment: string;
    score: number;
    confidence: number;
    timing?: Record<string, any>;
    option_recommendation?: Record<string, any>;
}

interface ApplyFilterResponse {
    passed_stocks: FilterResultStockSchema[];
    failed_stocks: FilterResultStockSchema[];
}
const FilterApiSchema = z.object({
    id: z.string().optional(),
    function_code: z.string().min(1, "Code is required"),
    variables: z.array(z.string()),
    type: z.string(),
    function_name: z.string().min(1, "Function name is required"),
    category: z.enum(["stocks", "crypto"]),
    isActive: z.boolean(),
});

export type FilterApiSchema = z.infer<typeof FilterApiSchema>;

// Define form schema
const formSchema = z.object({
    filters: z.array(z.string()).min(1, "Please select at least one filter."),
    buckets: z.array(z.string()).min(1, "Please select at least one bucket."),
    testMode: z.enum(["live", "backtest"]),
    backtestDateTime: z.string().optional(),
});

type ManualTestFormSchema = z.infer<typeof formSchema>;

export default function ManualTesting() {
    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            filters: [] as string[],
            buckets: [],
            testMode: "live",
            backtestDateTime: "",
        },
    })
    const testMode = form.watch("testMode");
    const { user, currentTradingMode } = useAuth();
    const { toast } = useToast();
    const [passedStocks, setPassedStocks] = useState<FilterResultStockSchema[]>([])
    const [failedStocks, setFailedStocks] = useState<FilterResultStockSchema[]>([])
    const allTestResults = [...passedStocks, ...failedStocks];
    const [sentimentResult, setSentimentResult] = useState<Record<string, SentimentStockData> | null>(null);
    const [selectedBucket, setSelectedBucket] = useState<string>("");
    const [adrResult, setAdrResult] = useState<Record<string, string | number> | null>(null);

    const { data: buckets, isLoading: bucketsLoading } = useQuery({
        queryKey: ["api/buckets", user?.userId, user?.role, currentTradingMode],
        queryFn: async () => {
            if (!user?.userId || !user?.role || !currentTradingMode) return [];
            const api_url = `api/buckets?userId=${user.userId}&role=${user.role}&category=${currentTradingMode}`;
            const res = await apiRequest<ApiResponse<Bucket[]>>("GET", api_url);
            return res.data;
        },
    })

    const { data: filters, isLoading: filterLoading } = useQuery<FilterApiSchema[]>({
        queryKey: ["api/filters", currentTradingMode],
        queryFn: async () => {
            const params = new URLSearchParams({
                category: currentTradingMode,
                code: "0"
            });
            const res = await apiRequest<ApiResponse<FilterApiSchema[]>>(
                "GET",
                `api/filters?${params.toString()}`
            );
            return res.data;
        },
        enabled: !!currentTradingMode,
    });

    const runTestMutation = useMutation({
        mutationFn: (data: ManualTestFormSchema) =>
            apiRequest<ApiResponse<ApplyFilterResponse>>(
                "POST",
                "api/apply-filters",
                {
                    filterIds: data.filters,
                    bucketIds: data.buckets,
                    testMode: data.testMode,
                    backtestDateTime: data.testMode === "backtest" ? data.backtestDateTime : undefined,
                    userId: user?.userId,
                },
                undefined, // params
                currentTradingMode === "crypto" ? "crypto" : "stocks"
            ),

        onSuccess: (response) => {
            setPassedStocks(response.data.passed_stocks);
            setFailedStocks(response.data.failed_stocks);
            toast({ title: "Success", description: "Filter test completed!" });
        },

        onError: () => {
            toast({
                title: "Error",
                description: "Failed to run filter test",
                variant: "destructive",
            });
        },
    });

    const adrMutation = useMutation({
        mutationFn: async () => {
            if (!user?.userId || !selectedBucket) throw new Error("Missing inputs");
            const res = await apiRequest<ApiResponse<Record<string, string | number>>>(
                "POST",
                `api/adr-analysis?userId=${user.userId}&bucket=${selectedBucket}`,
                undefined,
                undefined,
                currentTradingMode === "crypto" ? "crypto" : "stocks"
            );
            return res.data;
        },
        onSuccess: (data) => {
            toast({ title: "Success", description: "ADR analysis completed!" });
            setAdrResult(data);
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to run ADR analysis",
                variant: "destructive",
            });
        },
    });

    const sentimentMutation = useMutation({
        mutationFn: async () => {
            if (!user?.userId || !selectedBucket) throw new Error("Missing inputs");
            const res = await apiRequest<ApiResponse<Record<string, SentimentStockData>>>(
                "POST",
                `api/sentiment-analysis?userId=${user.userId}&bucket=${selectedBucket}`,
                undefined,
                undefined,
                currentTradingMode === "crypto" ? "crypto" : "stocks"
            );
            return res.data;
        },
        onSuccess: (data) => {
            setSentimentResult(data);
            toast({ title: "Success", description: "Sentiment analysis completed!" });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to run sentiment analysis",
                variant: "destructive",
            });
        },
    });

    useEffect(() => {
        form.reset({
            filters: [],
            buckets: [],
            testMode: "live",
            backtestDateTime: "",
        });

        setPassedStocks([]);
        setFailedStocks([]);
    }, [currentTradingMode]);

    if (filterLoading || bucketsLoading) {
        return (
            <div className="space-y-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="h-64 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    const onSubmit = (data: ManualTestFormSchema) => {
        runTestMutation.mutate(data);
    }


    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-4">
                    <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
                    Market Analysis Dashboard
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Comprehensive testing and analysis tools for your {currentTradingMode} trading strategies
                </p>
            </div>

            <Tabs defaultValue="filters" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-3 bg-white shadow-lg rounded-xl p-1">
                    <TabsTrigger
                        value="filters"
                        className="flex items-center space-x-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-lg transition-all"
                    >
                        <BarChart3 className="w-4 h-4" />
                        <span>Filter Testing</span>
                    </TabsTrigger>
                    <TabsTrigger
                        value="sentiment"
                        className="flex items-center space-x-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-lg transition-all"
                    >
                        <Brain className="w-4 h-4" />
                        <span>Sentiment</span>
                    </TabsTrigger>
                    <TabsTrigger
                        value="adr"
                        className="flex items-center space-x-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-lg transition-all"
                    >
                        <Activity className="w-4 h-4" />
                        <span>ADR</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="filters">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mb-4 pb-6">
                            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                                <CardContent className="pt-8">
                                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 space-y-4 md:space-y-0">
                                        <div className="flex items-start md:items-center space-x-3">
                                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                                                <Target className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl md:text-2xl font-bold text-gray-800">Filter Testing</h2>
                                                <p className="text-gray-600 text-sm md:text-base">Configure and run your strategy filters</p>
                                            </div>
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={runTestMutation.isPending}
                                            className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                                        >
                                            <Play className="w-5 h-5 mr-2" />
                                            {runTestMutation.isPending ? "Running Test..." : "Run Test"}
                                        </Button>
                                    </div>

                                    {/* Enhanced Filter and Bucket Selection */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                        <div className="space-y-3">
                                            <Label className="text-lg font-semibold text-gray-700 flex items-center">
                                                <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
                                                Select Filters
                                            </Label>
                                            <FormField
                                                control={form.control}
                                                name="filters"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className="w-full justify-between h-12 bg-white border-2 border-gray-200 hover:border-blue-300 rounded-xl"
                                                                >
                                                                    <span className="text-left">
                                                                        {field.value.length > 0
                                                                            ? `Selected (${field.value.length})`
                                                                            : "Select Filters"}
                                                                    </span>
                                                                    <Check className="ml-2 h-4 w-4 opacity-50" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-full p-3 max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl border-0">
                                                                <div className="flex justify-between mb-2">

                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => {
                                                                            const buyIds = filters
                                                                                ?.filter(f => f.id && f.type === "BUY")
                                                                                .map(f => f.id);
                                                                            field.onChange(buyIds);
                                                                        }}
                                                                    >
                                                                        Select All BUY
                                                                    </Button>

                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => {
                                                                            const sellIds = filters
                                                                                ?.filter(f => f.id && f.type === "SELL")
                                                                                .map(f => f.id);
                                                                            field.onChange(sellIds);
                                                                        }}
                                                                    >
                                                                        Select All SELL
                                                                    </Button>

                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => {
                                                                            const allIds = filters
                                                                                ?.filter(f => f.id)
                                                                                .map(f => f.id);
                                                                            field.onChange(allIds);
                                                                        }}
                                                                    >
                                                                        Select All
                                                                    </Button>

                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => field.onChange([])}
                                                                    >
                                                                        Clear
                                                                    </Button>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {filters?.slice().sort((a, b) => a.function_name.localeCompare(b.function_name)).map((filter) => {
                                                                        if (!filter.id) return null;
                                                                        const isChecked = field.value.includes(filter.id);
                                                                        return (
                                                                            <div
                                                                                key={filter.id}
                                                                                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 cursor-pointer border border-transparent hover:border-blue-200 transition-all"
                                                                                onClick={() => {
                                                                                    const newValue = isChecked
                                                                                        ? field.value.filter((v) => v !== filter.id)
                                                                                        : [...field.value, filter.id];
                                                                                    field.onChange(newValue);
                                                                                }}
                                                                            >
                                                                                <Checkbox
                                                                                    checked={isChecked}
                                                                                    onCheckedChange={() => { }}
                                                                                    className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                                                                                />
                                                                                <span className="font-medium text-gray-700">{filter.function_name}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </FormItem>
                                                )}
                                            />
                                            {form.formState.errors.filters && (
                                                <p className="text-sm text-red-500 font-medium">
                                                    {form.formState.errors.filters.message}
                                                </p>
                                            )}
                                        </div>

                                        <div className="space-y-3">
                                            <Label className="text-lg font-semibold text-gray-700 flex items-center">
                                                <Target className="w-5 h-5 mr-2 text-purple-500" />
                                                Select Buckets
                                            </Label>
                                            <FormField
                                                control={form.control}
                                                name="buckets"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className="w-full justify-between h-12 bg-white border-2 border-gray-200 hover:border-purple-300 rounded-xl"
                                                                >
                                                                    <span className="text-left">
                                                                        {field.value.length > 0
                                                                            ? `Selected (${field.value.length})`
                                                                            : "Select Buckets"}
                                                                    </span>
                                                                    <Check className="ml-2 h-4 w-4 opacity-50" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-full p-3 max-h-80 overflow-y-auto bg-white rounded-xl shadow-xl border-0">
                                                                <div className="flex justify-between mb-2">
                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => {
                                                                            const allIds = buckets?.map(b => b._id);
                                                                            field.onChange(allIds);
                                                                        }}
                                                                    >
                                                                        Select All
                                                                    </Button>

                                                                    <Button
                                                                        variant="ghost"
                                                                        className="text-sm"
                                                                        onClick={() => field.onChange([])}
                                                                    >
                                                                        Clear
                                                                    </Button>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {buckets?.slice().sort((a, b) => a.name.localeCompare(b.name)).map((bucket) => {
                                                                        const isChecked = field.value.includes(bucket._id);
                                                                        return (
                                                                            <div
                                                                                key={bucket._id}
                                                                                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-purple-50 cursor-pointer border border-transparent hover:border-purple-200 transition-all"
                                                                                onClick={() => {
                                                                                    const newValue = isChecked
                                                                                        ? field.value.filter((v) => v !== bucket._id)
                                                                                        : [...field.value, bucket._id];
                                                                                    field.onChange(newValue);
                                                                                }}
                                                                            >
                                                                                <Checkbox
                                                                                    checked={isChecked}
                                                                                    onCheckedChange={() => { }}
                                                                                    className="data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                                                                                />
                                                                                <span className="font-medium text-gray-700">{bucket.name}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </FormItem>
                                                )}
                                            />
                                            {form.formState.errors.buckets && (
                                                <p className="text-sm text-red-500 font-medium">
                                                    {form.formState.errors.buckets.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Enhanced Test Mode Selection */}
                                    <div className="mb-8">
                                        <Label className="text-lg font-semibold text-gray-700 mb-4 block">Test Mode</Label>
                                        <FormField
                                            control={form.control}
                                            name="testMode"
                                            render={({ field }) => (
                                                <RadioGroup
                                                    onValueChange={field.onChange}
                                                    value={field.value}
                                                    className="grid grid-cols-2 gap-6"
                                                >
                                                    <div className="flex items-center space-x-3 p-4 rounded-xl border-2 border-gray-200 hover:border-green-300 transition-colors">
                                                        <RadioGroupItem value="live" id="live" className="data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500" />
                                                        <Label htmlFor="live" className="font-medium text-gray-700 cursor-pointer">Live Test</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-3 p-4 rounded-xl border-2 border-gray-200 hover:border-orange-300 transition-colors">
                                                        <RadioGroupItem value="backtest" id="backtest" className="data-[state=checked]:border-orange-500 data-[state=checked]:bg-orange-500" />
                                                        <Label htmlFor="backtest" className="font-medium text-gray-700 cursor-pointer">Backtest</Label>
                                                    </div>
                                                </RadioGroup>
                                            )}
                                        />
                                    </div>

                                    {/* Backtest Configuration */}
                                    {testMode === "backtest" && (
                                        <div className="mb-8">
                                            <FormField
                                                control={form.control}
                                                name="backtestDateTime"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label className="text-lg font-semibold text-gray-700 mb-2 block">
                                                            Backtest Date & Time
                                                        </Label>
                                                        <FormControl>
                                                            <Input
                                                                type="datetime-local"
                                                                {...field}
                                                                className="w-full max-w-md h-12 border-2 border-gray-200 rounded-xl"
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                For a full strategy backtest with a PDF report, use{" "}
                                                <a href="/backtesting" className="text-blue-600 underline">
                                                    Backtesting
                                                </a>
                                                .
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            {allTestResults.length > 0 && (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Passed Results */}
                                        <Card className="shadow-xl border-0 bg-gradient-to-br from-green-50 to-white">
                                            <CardContent className="pt-6">
                                                <div className="flex items-center space-x-3 mb-6">
                                                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                                                        <TrendingUp className="w-5 h-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-800">Verified Results</h3>
                                                        <p className="text-green-600 font-medium">{passedStocks.length} signals passed</p>
                                                    </div>
                                                </div>
                                                <ResultGrid results={passedStocks} isPassed={true} />
                                            </CardContent>
                                        </Card>

                                        {/* Failed Results */}
                                        <Card className="shadow-xl border-0 bg-gradient-to-br from-red-50 to-white">
                                            <CardContent className="pt-6">
                                                <div className="flex items-center space-x-3 mb-6">
                                                    <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                                                        <TrendingDown className="w-5 h-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-800">Non-Verified Results</h3>
                                                        <p className="text-red-600 font-medium">{failedStocks.length} signals failed</p>
                                                    </div>
                                                </div>
                                                <ResultGrid results={failedStocks} isPassed={false} />
                                            </CardContent>
                                        </Card>
                                    </div>


                                    <SortableGPTable passedStocks={passedStocks} failedStocks={failedStocks} />


                                    {/* Enhanced Summary Card */}
                                    <Card className="shadow-xl border-0 bg-gradient-to-r from-blue-50 via-white to-purple-50">
                                        <CardContent className="pt-8">
                                            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Test Summary</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                                <div className="text-center p-4 bg-white rounded-xl shadow-md">
                                                    <p className="text-3xl font-bold text-gray-900 mb-2">{allTestResults.length}</p>
                                                    <p className="text-sm text-gray-600 font-medium">Total Tested</p>
                                                </div>
                                                <div className="text-center p-4 bg-green-50 rounded-xl shadow-md">
                                                    <p className="text-3xl font-bold text-green-600 mb-2">{passedStocks.length}</p>
                                                    <p className="text-sm text-gray-600 font-medium">Passed</p>
                                                </div>
                                                <div className="text-center p-4 bg-red-50 rounded-xl shadow-md">
                                                    <p className="text-3xl font-bold text-red-600 mb-2">{failedStocks.length}</p>
                                                    <p className="text-sm text-gray-600 font-medium">Failed</p>
                                                </div>
                                                <div className="text-center p-4 bg-blue-50 rounded-xl shadow-md">
                                                    <p className="text-3xl font-bold text-blue-600 mb-2">
                                                        {Math.round((passedStocks.length / allTestResults.length) * 100)}%
                                                    </p>
                                                    <p className="text-sm text-gray-600 font-medium">Success Rate</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </>
                            )}

                        </form>
                    </Form>
                </TabsContent>

                <TabsContent value="sentiment">
                    <Card className="mt-4 rounded-2xl overflow-hidden">
                        <CardContent className="pt-6 space-y-8">

                            {/* Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-md">
                                    <Brain className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-extrabold text-gray-900">Sentiment Analysis</h2>
                                    <p className="text-gray-600 text-sm sm:text-base">
                                        Analyze overall and symbol-level market sentiment
                                    </p>
                                </div>
                            </div>

                            {/* Bucket selection */}
                            <div className="space-y-3">
                                <Label className="text-lg font-semibold text-gray-800">Select Bucket</Label>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-between h-14 rounded-2xl border-2 bg-gradient-to-r from-purple-50 to-pink-50 hover:border-purple-400"
                                        >
                                            <span className="truncate text-gray-800">
                                                {selectedBucket
                                                    ? buckets?.find(b => b._id === selectedBucket)?.name || "Select bucket"
                                                    : "Select bucket for sentiment analysis"}
                                            </span>
                                            <ChevronDown className="h-5 w-5 opacity-60" />
                                        </Button>
                                    </PopoverTrigger>

                                    <PopoverContent className="w-full p-0 max-h-80 overflow-hidden bg-white rounded-2xl shadow-2xl border-0">
                                        <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
                                            <h4 className="font-semibold text-gray-800">Available Buckets</h4>
                                        </div>

                                        <div className="max-h-64 overflow-y-auto p-3 space-y-2">
                                            {buckets?.map(bucket => (
                                                <div
                                                    key={bucket._id}
                                                    onClick={() => setSelectedBucket(bucket._id)}
                                                    className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${selectedBucket === bucket._id
                                                        ? "bg-purple-50 border-2 border-purple-200"
                                                        : "hover:bg-gray-50 border-2 border-transparent"
                                                        }`}
                                                >
                                                    <Checkbox
                                                        checked={selectedBucket === bucket._id}
                                                        className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                                                    />
                                                    <div className="flex-1">
                                                        <span className="font-medium text-gray-800">{bucket.name}</span>
                                                        <p className="text-xs text-gray-500 mt-1">Portfolio bucket</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Run Button */}
                            <Button
                                onClick={() => sentimentMutation.mutate()}
                                disabled={sentimentMutation.isPending || !selectedBucket}
                                className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-rose-600 text-white font-semibold shadow-xl hover:scale-[1.02] hover:shadow-2xl transition-all disabled:opacity-50"
                            >
                                {sentimentMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                                        Analyzing sentiment…
                                    </>
                                ) : (
                                    <>
                                        <Play className="w-5 h-5 mr-3" />
                                        Execute Sentiment Analysis
                                    </>
                                )}
                            </Button>

                            {/* RESULTS */}
                            {sentimentResult && (
                                <div className="space-y-8">

                                    {/* Title */}
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                                            <BarChart3 className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                                            Sentiment Analysis Results
                                        </h3>
                                    </div>

                                    {/* Result cards */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                                        {Object.entries(sentimentResult).map(([symbol, data]) => {
                                            if (symbol === "FINAL_SENTIMENT") return null;

                                            const { sentiment, score, confidence, timing, option_recommendation } = data;

                                            const sentimentColor =
                                                sentiment === "Bullish"
                                                    ? "from-green-500 to-emerald-600"
                                                    : sentiment === "Bearish"
                                                        ? "from-red-500 to-pink-600"
                                                        : "from-gray-500 to-slate-600";

                                            return (
                                                <div
                                                    key={symbol}
                                                    className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-200"
                                                >
                                                    <div className={`bg-gradient-to-r ${sentimentColor} p-4`}>
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-bold text-white text-lg truncate">{symbol}</span>
                                                            <span className="bg-white/20 px-3 py-1 rounded-full text-white font-semibold text-sm">
                                                                {sentiment}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="p-4 space-y-4">

                                                        {/* Score */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-gray-600 font-medium">Sentiment Score</span>
                                                            <span className="font-bold text-gray-900">{score?.toFixed(3)}</span>
                                                        </div>

                                                        {/* Confidence bar */}
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-gray-600 font-medium">Confidence</span>

                                                            <div className="flex items-center space-x-2">
                                                                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all"
                                                                        style={{ width: `${(confidence || 0) * 100}%` }}
                                                                    />
                                                                </div>
                                                                <span className="font-bold text-gray-900">
                                                                    {(confidence * 100).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Timing */}
                                                        {timing && Object.keys(timing).length > 0 && (
                                                            <div className="pt-2 border-t border-gray-100">
                                                                <span className="text-xs font-medium text-gray-500 uppercase">
                                                                    Timing Signal
                                                                </span>
                                                                <p className="text-sm text-gray-700 mt-1 font-mono bg-gray-50 p-2 rounded break-all">
                                                                    {JSON.stringify(timing)}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Option Recommendation */}
                                                        {option_recommendation && (
                                                            <div className="pt-2 border-t border-gray-100">
                                                                <span className="text-xs font-medium text-gray-500 uppercase">
                                                                    Option Recommendation
                                                                </span>
                                                                <p className="text-sm text-gray-700 mt-1 font-mono bg-gray-50 p-2 rounded break-all">
                                                                    {JSON.stringify(option_recommendation)}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* FINAL SENTIMENT */}
                                    {sentimentResult.FINAL_SENTIMENT && (
                                        <Card className="shadow-2xl border-0 rounded-2xl overflow-hidden bg-gradient-to-r from-indigo-50 to-purple-50">
                                            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-8 text-center space-y-4">
                                                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto border border-white/30 shadow">
                                                    <Brain className="w-8 h-8 text-white" />
                                                </div>

                                                <h3 className="text-2xl font-bold text-white">Overall Market Sentiment</h3>

                                                <div
                                                    className={`inline-flex items-center space-x-3 px-6 py-3 rounded-full font-bold text-lg ${sentimentResult.FINAL_SENTIMENT.sentiment === "Bullish"
                                                        ? "bg-green-500 text-white"
                                                        : "bg-red-500 text-white"
                                                        }`}
                                                >
                                                    {sentimentResult.FINAL_SENTIMENT.sentiment === "Bullish" ? (
                                                        <TrendingUp className="w-5 h-5" />
                                                    ) : (
                                                        <TrendingDown className="w-5 h-5" />
                                                    )}
                                                    <span>{sentimentResult.FINAL_SENTIMENT.sentiment}</span>
                                                </div>
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="adr">
                    <Card className="mt-4 overflow-hidden">

                        {/* HEADER */}
                        <div className="bg-gradient-to-r from-green-500 via-teal-500 to-cyan-600 p-4 md:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                                        <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                                    </div>

                                    <div>
                                        <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                                            ADR Analysis
                                        </h2>
                                        <p className="text-green-100 text-sm sm:text-base">
                                            Advance–Decline Ratio • Market Breadth
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <CardContent className="pt-4 md:pt-6 space-y-6">

                            {/* PORTFOLIO SELECT */}
                            <div className="space-y-3">

                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl flex items-center justify-center">
                                        <Users className="w-5 h-5 text-white" />
                                    </div>

                                    <div>
                                        <Label className="text-base sm:text-lg font-semibold text-gray-800">
                                            Portfolio Selection
                                        </Label>
                                        <p className="text-xs sm:text-sm text-gray-600">
                                            Choose a bucket for ADR analysis
                                        </p>
                                    </div>
                                </div>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-between h-12 sm:h-14 bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 hover:border-green-400 rounded-xl font-medium text-sm sm:text-base"
                                        >
                                            <div className="flex items-center gap-2 truncate">
                                                {selectedBucket && (
                                                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                                        <Check className="w-3 h-3 text-white" />
                                                    </div>
                                                )}
                                                <span className="truncate">
                                                    {selectedBucket
                                                        ? buckets?.find((b) => b._id === selectedBucket)?.name ||
                                                        "Select Portfolio"
                                                        : "Select Portfolio for Analysis"}
                                                </span>
                                            </div>
                                            <ChevronDown className="h-5 w-5 text-gray-400" />
                                        </Button>
                                    </PopoverTrigger>

                                    <PopoverContent className="w-[90vw] sm:w-full p-0 max-h-80 overflow-hidden bg-white rounded-2xl shadow-xl border-0">

                                        <div className="p-3 bg-gradient-to-r from-green-50 to-teal-50 border-b">
                                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                Available Portfolios
                                            </h4>
                                        </div>

                                        <div className="max-h-64 overflow-y-auto p-2 sm:p-3 space-y-2">
                                            {buckets?.map((bucket) => (
                                                <div
                                                    key={bucket._id}
                                                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200
                    ${selectedBucket === bucket._id
                                                            ? "bg-green-50 border-2 border-green-200"
                                                            : "hover:bg-gray-50 border-2 border-transparent"
                                                        }`}
                                                    onClick={() => setSelectedBucket(bucket._id)}
                                                >
                                                    <Checkbox
                                                        checked={selectedBucket === bucket._id}
                                                        className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <span className="font-medium text-gray-800 truncate">
                                                            {bucket.name}
                                                        </span>
                                                        <p className="text-xs text-gray-500 mt-1 truncate">
                                                            Asset Portfolio
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* RUN BUTTON — sticky on mobile */}
                            <div className="sm:static sticky bottom-0 bg-white/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md py-2">
                                <Button
                                    onClick={() => adrMutation.mutate()}
                                    disabled={adrMutation.isPending || !selectedBucket}
                                    size="lg"
                                    className="w-full h-12 sm:h-14 bg-gradient-to-r from-green-500 via-teal-500 to-cyan-600 hover:from-green-600 hover:via-teal-600 hover:to-cyan-700 text-white rounded-xl shadow-md sm:shadow-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {adrMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Analyzing…
                                        </>
                                    ) : (
                                        <>
                                            <Play className="w-5 h-5 mr-2" />
                                            Execute ADR Analysis
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* RESULTS */}
                            {adrResult && (
                                <div className="space-y-4">
                                    <h3 className="text-base sm:text-lg font-semibold">ADR Results</h3>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                                        {Object.entries(adrResult).map(([key, value]) => {
                                            // ARRAY VALUES
                                            if (Array.isArray(value)) {
                                                return (
                                                    <div
                                                        key={key}
                                                        className="p-4 rounded-xl shadow bg-white col-span-1 sm:col-span-2 lg:col-span-3"
                                                    >
                                                        <span className="font-medium block mb-2">{key}</span>

                                                        <div className="flex flex-wrap gap-2">
                                                            {value.length > 0 ? (
                                                                value.map((stock) => (
                                                                    <span
                                                                        key={stock}
                                                                        className={`px-2 py-1 rounded-lg text-xs sm:text-sm font-semibold
                              ${key.includes("ADVANCING")
                                                                                ? "bg-green-100 text-green-700"
                                                                                : "bg-red-100 text-red-700"
                                                                            }`}
                                                                    >
                                                                        {stock}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-gray-500 italic text-sm">
                                                                    None
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            // SCALAR VALUES
                                            return (
                                                <div
                                                    key={key}
                                                    className="p-4 rounded-xl shadow bg-white flex items-center justify-between"
                                                >
                                                    <span className="font-medium text-sm sm:text-base">{key}</span>
                                                    <span
                                                        className={`font-bold text-sm sm:text-base
                      ${value === "Bullish"
                                                                ? "text-green-600"
                                                                : value === "Bearish"
                                                                    ? "text-red-600"
                                                                    : "text-gray-600"
                                                            }`}
                                                    >
                                                        {String(value)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}